import {EyeOutlined} from '@ant-design/icons';
import {Timeline as $Timeline, Button, Card, Modal, Typography} from 'antd';
import {
  useMasterAccountEventsQuery,
  MasterAccountEvent,
  useOnUpdateEventsSubscription
} from 'api/generated';
import {AnchorLink, EmailLink, Stack} from 'components';
import {Code} from 'components/Code';
import {formatDate} from 'lib/formaters';
import {getChangedKeys, prettifyEvent} from 'lib/utils';
import React, {useEffect, useState} from 'react';
import {
  MasterAccountEventWithChanged,
  getEventItem,
  getMasterAccountEventDescription,
  sortChangedKeys
} from 'routes/MasterAccount/utils';
import styled from 'styled-components';

const {Paragraph} = Typography;

type Props = {
  masterAccountId: string;
};

const Timeline = styled($Timeline)`
  margin: 10px 0 0 3px !important;
  & .ant-timeline-item-last {
    padding-bottom: 0;
    & > .ant-timeline-item-content {
      min-height: auto;
    }
  }
`;

const Description = styled.div`
  padding: 0.2em 0.4em;
  margin: 0.2em 0;
  background: rgba(150, 150, 150, 0.1);
  border: 1px solid rgba(100, 100, 100, 0.2);
  border-radius: 3px;
`;

const getTitle = (event: MasterAccountEventWithChanged) => {
  const item = getEventItem(event);
  if (!item) return <></>;

  const details = (event as MasterAccountEvent).object?.traceDetails;

  if (details?.userEmail) {
    return (
      <>
        {item.title} by <EmailLink email={details.userEmail}>{details.userName}</EmailLink>
      </>
    );
  } else if (details?.source) {
    return (
      <>
        {item.title} ({details.source})
      </>
    );
  }
  return item.title;
};

export const MasterAccountTimeline: React.FC<Props> = ({masterAccountId}) => {
  const [selectedEvent, setSelectedEvent] = useState<MasterAccountEventWithChanged | null>(null);
  const [events, setEvents] = useState<MasterAccountEventWithChanged[]>([]);
  const [isInfoOpen, setInfoOpen] = useState(false);
  const [folded, setFolded] = useState(false);
  const size = 10;
  const [from, setFrom] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const {loading, refetch, fetchMore} = useMasterAccountEventsQuery({
    variables: {masterAccountId, size}
  });
  const [hasMoreEvents, setHasMoreEvents] = useState(false);
  const [hasMoreThanInitial, setHasMoreThanInitial] = useState(false);

  useOnUpdateEventsSubscription({
    variables: {accountId: masterAccountId},
    onSubscriptionData: () => {
      refetch({size: size + from + 1});
    }
  });

  const handleInfoClose = () => setInfoOpen(false);

  const loadMore = () => {
    if (folded) {
      setFolded(false);
      setHasMoreThanInitial(true);
      return;
    }
    setFrom((prev) => prev + size);
    setLoadingMore(true);
    fetchMore({variables: {from}}).then((result) => {
      setLoadingMore(false);
      if (result.data?.masterAccountEvents?.items) {
        setEvents((prev) => {
          const eventsWithChangedKeys = result.data.masterAccountEvents.items.map(
            (item, i, arr) => ({
              ...item,
              changed: !!arr[i++]
                ? getChangedKeys(item, arr[i++])
                    .map((key) => key.replace(/^object\./, ''))
                    .sort(sortChangedKeys)
                : []
            })
          ) as MasterAccountEventWithChanged[];

          return [...prev, ...eventsWithChangedKeys].sort((a, b) =>
            a.createdAt > b.createdAt ? -1 : 1
          );
        });

        const eventsLength = events.length + result.data.masterAccountEvents.items.length;
        const totalEvents = result.data?.masterAccountEvents.total;

        setHasMoreEvents(eventsLength < totalEvents);
        setHasMoreThanInitial(eventsLength > size);
      }
    });
  };

  const foldEvents = () => {
    setFolded(true);
    setHasMoreThanInitial(false);
  };

  useEffect(() => {
    refetch()
      .then(({data}) => {
        return Promise.resolve(data.masterAccountEvents?.items);
      })
      .then(loadMore);
  }, []);

  useEffect(() => {
    if (folded) {
      const foldButton = document.getElementById('show-more-button') as HTMLButtonElement;
      foldButton?.scrollIntoView({block: 'center', behavior: 'smooth'});
    }
  }, [folded]);

  return (
    <>
      <Card title="Timeline" size="small" style={{marginBottom: 20}} loading={loading}>
        <Timeline>
          {events.slice(0, folded ? size : undefined).map((event, i) => {
            const item = getEventItem(event);
            if (!item) return <></>;
            const description = getMasterAccountEventDescription(event);
            return (
              <Timeline.Item
                key={event.id}
                dot={item.icon ? <item.icon style={{color: item.color}} /> : null}>
                <Paragraph style={{marginBottom: 0}}>
                  {getTitle(event)}{' '}
                  <AnchorLink
                    onClick={() => {
                      setSelectedEvent(event);
                      setInfoOpen(true);
                    }}>
                    <EyeOutlined />
                  </AnchorLink>
                </Paragraph>
                {description && <Description>{description}</Description>}
                <Paragraph type="secondary" style={{fontSize: '12px', marginBottom: 0}}>
                  {formatDate(event.createdAt, 'X', true, true)}
                </Paragraph>
              </Timeline.Item>
            );
          })}
        </Timeline>
        <Stack justify="center" fullWidth style={{marginTop: 10}}>
          {hasMoreThanInitial && <Button onClick={foldEvents}>Show less</Button>}
          {(hasMoreEvents || folded) && (
            <Button onClick={() => loadMore()} loading={loadingMore} id="show-more-button">
              Show more
            </Button>
          )}
        </Stack>
      </Card>
      <Modal
        title="Event data"
        width={800}
        open={isInfoOpen}
        footer={false}
        onCancel={handleInfoClose}>
        {selectedEvent && <Code>{prettifyEvent(selectedEvent)}</Code>}
      </Modal>
    </>
  );
};
